import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Trash2, Building2 } from "lucide-react";
import { Button } from "../../../ui/components/Button";
import { ConfirmModal } from "../../../ui/components/ConfirmModal";
import { DisabledAccountStatusPanel } from "../common/DisabledAccountStatusPanel";
import * as userApi from "../../lib/api/users";
import { getApiErrorMessage } from "../../lib/api/api-error";
import type { DisabledBySnapshot } from "../../lib/disabled-status";
import * as styles from "../users/UserAccountDangerZone.css";

type PendingAction = "disable" | "delete" | "restore";

const HARD_DELETE_CONFIRM_PHRASE = "delete";

export interface OrganizationAccountDangerZoneProps {
  orgId: string;
  orgName: string;
  accessToken: string;
  isDisabled: boolean;
  disabledSnapshot: DisabledBySnapshot;
  canDisable: boolean;
  canHardDelete: boolean;
  canRestore: boolean;
  onOrganizationChanged?: () => void;
}

export function OrganizationAccountDangerZone({
  orgId,
  orgName,
  accessToken,
  isDisabled,
  disabledSnapshot,
  canDisable,
  canHardDelete,
  canRestore,
  onOrganizationChanged,
}: OrganizationAccountDangerZoneProps) {
  const navigate = useNavigate();
  const [pendingAction, setPendingAction] = React.useState<PendingAction | null>(
    null,
  );
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [expanded, setExpanded] = React.useState(false);

  const closeModal = React.useCallback(() => {
    if (loading) return;
    setPendingAction(null);
    setError(null);
  }, [loading]);

  const handleConfirm = React.useCallback(async () => {
    if (!pendingAction) return;
    setLoading(true);
    setError(null);
    try {
      if (pendingAction === "disable") {
        await userApi.disableOrganization(accessToken, orgId);
      } else if (pendingAction === "delete") {
        await userApi.deleteOrganizationPermanently(accessToken, orgId);
        navigate({ to: "/workspace/organizations", replace: true });
        return;
      } else {
        await userApi.restoreOrganization(accessToken, orgId);
      }
      setPendingAction(null);
      onOrganizationChanged?.();
    } catch (e) {
      setError(getApiErrorMessage(e, "Something went wrong. Try again."));
    } finally {
      setLoading(false);
    }
  }, [pendingAction, accessToken, orgId, navigate, onOrganizationChanged]);

  const modalCopy = React.useMemo(() => {
    if (!pendingAction) return null;
    const subject = orgName || "this organization";

    if (pendingAction === "disable") {
      return {
        title: "Disable organization?",
        body: `Disable ${subject}? Members will no longer see or interact with this organization. All data and memberships will be kept. A super-admin can restore it later.`,
        confirmLabel: "Disable organization",
        confirmColor: "danger" as const,
      };
    }
    if (pendingAction === "delete") {
      return {
        title: "Permanently delete organization?",
        body: `Permanently delete ${subject}? This removes the organization record and destroys all related data. This cannot be undone. The organization name will become available for a new organization.`,
        confirmLabel: "Delete permanently",
        confirmColor: "danger" as const,
      };
    }
    return {
      title: "Restore organization?",
      body: `Restore ${subject}? Members will regain access with their previous roles and memberships.`,
      confirmLabel: "Restore organization",
      confirmColor: "primary" as const,
    };
  }, [pendingAction, orgName]);

  if (isDisabled) {
    const disabledPanel = (
      <DisabledAccountStatusPanel
        kind="organization"
        snapshot={disabledSnapshot}
        canRestore={canRestore}
        restoreLabel="Restore organization"
        restoreIcon={<Building2 size={16} />}
        onRestore={
          canRestore
            ? () => {
                setError(null);
                setPendingAction("restore");
              }
            : undefined
        }
      />
    );

    if (!canRestore && !canHardDelete) {
      return disabledPanel;
    }

    return (
      <>
        {disabledPanel}
        {canHardDelete && (
          <div className={styles.dangerZone} style={{ marginTop: 16 }}>
            <button type="button" className={styles.dangerZoneToggleBtn} onClick={() => setExpanded(e => !e)} aria-expanded={expanded}>
              Danger zone
              <svg className={`${styles.dangerZoneCaret} ${expanded ? styles.dangerZoneCaretOpen : ''}`} width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
            </button>
            {expanded && <>
            <p className={styles.dangerZoneDescription}>
              Permanently delete this organization and all related data.
            </p>
            <div className={styles.dangerZoneActions}>
              <Button
                type="button"
                color="danger"
                variant="solid"
                iconLeft={<Trash2 size={16} />}
                onClick={() => {
                  setError(null);
                  setPendingAction("delete");
                }}
              >
                Delete organization permanently
              </Button>
            </div>
            </>}
          </div>
        )}
        {pendingAction && modalCopy && (
          <ConfirmModal
            open
            title={modalCopy.title}
            body={modalCopy.body}
            confirmLabel={modalCopy.confirmLabel}
            confirmColor={modalCopy.confirmColor}
            confirmPhrase={
              pendingAction === "delete" ? HARD_DELETE_CONFIRM_PHRASE : undefined
            }
            loading={loading}
            error={error}
            onCancel={closeModal}
            onConfirm={() => void handleConfirm()}
          />
        )}
      </>
    );
  }

  if (!canDisable && !canHardDelete) {
    return null;
  }

  return (
    <>
      <div className={styles.dangerZone}>
        <button type="button" className={styles.dangerZoneToggleBtn} onClick={() => setExpanded(e => !e)} aria-expanded={expanded}>
          Danger zone
          <svg className={`${styles.dangerZoneCaret} ${expanded ? styles.dangerZoneCaretOpen : ''}`} width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
        </button>
        {expanded && <>
        <p className={styles.dangerZoneDescription}>
          Disable or permanently delete this organization.
        </p>
        <div className={styles.dangerZoneActions}>
          {canDisable && (
            <Button
              type="button"
              color="danger"
              variant="outline"
              iconLeft={<Building2 size={16} />}
              onClick={() => {
                setError(null);
                setPendingAction("disable");
              }}
            >
              Disable organization
            </Button>
          )}
          {canHardDelete && (
            <Button
              type="button"
              color="danger"
              variant="solid"
              iconLeft={<Trash2 size={16} />}
              onClick={() => {
                setError(null);
                setPendingAction("delete");
              }}
            >
              Delete organization permanently
            </Button>
          )}
        </div>
        </>}
      </div>
      {pendingAction && modalCopy && (
        <ConfirmModal
          open
          title={modalCopy.title}
          body={modalCopy.body}
          confirmLabel={modalCopy.confirmLabel}
          confirmColor={modalCopy.confirmColor}
          confirmPhrase={
            pendingAction === "delete" ? HARD_DELETE_CONFIRM_PHRASE : undefined
          }
          loading={loading}
          error={error}
          onCancel={closeModal}
          onConfirm={() => void handleConfirm()}
        />
      )}
    </>
  );
}
